<template>
  <div class="plan-settings-platforms">
    <DataTable
      :editingRows.sync="editingRows"
      :value="sortedPlatforms"
      editMode="row"
      dataKey="key"
      :autoLayout="true"
      class="categories-table"
      @row-edit-save="onRowEditSave"
    >
      <header>
        <div class="p-d-flex p-ai-center">
          <h2 :class="{'is-lead': showLeadIcon}">Platforms</h2>
          <span
            v-tooltip.bottom="platformsHelpText"
            class="pi pi-question-circle tooltip-color p-ml-2"
          ></span>
        </div>
      </header>
      <Column field="name" bodyClass="data-column-80">
        <template #body="slotProps">
          <div>
            {{ slotProps.data.name }}
            <span v-if="showIds" class="id-value"> ({{ slotProps.data.id.intID }})</span>
          </div>
        </template>
        <template #editor="slotProps">
          <InputText v-model="slotProps.data.name" />
        </template>
      </Column>
      <Column
        v-if="canEdit"
        :rowEditor="true"
        bodyClass="data-column-10"
      ></Column>
      <Column
        v-if="canEdit"
        bodyClass="data-column-10"
      >
        <template #body="slotProps">
          <Button
            icon="pi pi-trash"
            class="p-button-text p-button-danger"
            :disabled="deletingPlatformIntIds.includes(slotProps.data.id.intID)"
            @click="onDeleteClick($event, slotProps.data)"
          />
        </template>
        <template #editor>
          <Button
            icon="pi pi-trash"
            class="p-button-text p-button-danger"
            disabled
          />
        </template>
      </Column>

      <template #empty>
        <h3>No platforms exist.</h3>
      </template>
    </DataTable>

    <div v-if="canEdit" class="add-initiative-category-container">
      <div class="p-field">
        <InputText
          v-model="newPlatformName"
          placeholder="New Platform"
          @keyup.enter="addPlatform"
        />
      </div>
      <Button
        label="New Platform"
        icon="pi pi-plus-circle"
        class="p-button-text"
        :disabled="isCreatingPlatform"
        @click="addPlatform"
      />
    </div>

    <p v-if="isNestedPlan" class="platforms-lead-only-note">
      Platforms can only be edited within the Lead Plan.
    </p>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Tooltip from 'primevue/tooltip'
import ID from '@/models/ID'
import Plan from '@/models/Plan'
import TacticPlatform from '@/models/TacticPlatform'
import {AlertMessageSeverity} from '@/models/AlertMessage'
import {
  validatePlatformName,
  platformDeleteMessage,
  isNameViolation,
  renamePlatformInPlan,
  PLATFORM_NAME_DUPLICATE_MESSAGE,
} from '@/utils/platformManagement'

interface RowEditSaveEvent {
  newData: TacticPlatform
  data: TacticPlatform
}

export default Vue.extend({
  name: 'PlanSettingsPlatforms',
  directives: {
    tooltip: Tooltip,
  },
  props: {
    canEdit: {type: Boolean, default: false},
    isNestedPlan: {type: Boolean, default: false},
    showLeadIcon: {type: Boolean, default: false},
    showIds: {type: Boolean, default: false},
  },
  data: () => {
    return {
      editingRows: [] as TacticPlatform[],
      newPlatformName: '' as string,
      isCreatingPlatform: false as boolean,
      deletingPlatformIntIds: [] as number[],
      platformsHelpText:
        'Platforms are the third party media, publishing and advertising companies such as Instagram, The New York Times, and Clear Channel Outdoor, that you use to promote your brand.',
    }
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    sortedPlatforms(): TacticPlatform[] {
      return [...this.currentPlan.tacticPlatforms].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})
      )
    },
  },
  methods: {
    showWarning(summary: string) {
      Vue.prototype.$toast.add({
        severity: AlertMessageSeverity.warn,
        summary,
        life: 4000,
      })
    },
    showSaveError(error: unknown) {
      this.showWarning(
        isNameViolation(error) ? PLATFORM_NAME_DUPLICATE_MESSAGE : 'Error saving platform.'
      )
    },
    addPlatform() {
      // Ignore repeat clicks or Enter presses while a create is in flight
      if (this.isCreatingPlatform) {
        return
      }
      const validationMessage = validatePlatformName(
        this.newPlatformName,
        this.currentPlan.tacticPlatforms,
        0
      )
      if (validationMessage) {
        this.showWarning(validationMessage)
        return
      }

      const newPlatform = new TacticPlatform(
        new ID(),
        this.$store.getters.currentUser.id,
        this.newPlatformName.trim(),
        this.currentPlan.id
      )
      this.isCreatingPlatform = true
      this.$store.getters.services.channels
        .createTacticPlatform(newPlatform)
        .then(
          (returnedPlatform: TacticPlatform) => {
            this.currentPlan.tacticPlatforms.push(returnedPlatform)
            this.newPlatformName = ''
          },
          (error: unknown) => this.showSaveError(error)
        )
        .finally(() => {
          this.isCreatingPlatform = false
        })
    },
    onRowEditSave(event: RowEditSaveEvent) {
      const savedPlatform = this.currentPlan.tacticPlatforms.find(
        (platform) => platform.id.intID === event.data.id.intID
      )
      if (!savedPlatform) {
        return
      }

      const newName = event.newData.name
      if (newName.trim() === savedPlatform.name) {
        return
      }
      const validationMessage = validatePlatformName(
        newName,
        this.currentPlan.tacticPlatforms,
        savedPlatform.id.intID
      )
      if (validationMessage) {
        this.showWarning(validationMessage)
        return
      }

      const updatedPlatform = new TacticPlatform(
        savedPlatform.id.clone(),
        savedPlatform.creatorId.clone(),
        newName.trim(),
        this.currentPlan.id.clone(),
        savedPlatform.description
      )
      this.$store.getters.services.channels
        .updateTacticPlatform(updatedPlatform)
        .then(
          () => {
            // Tactics share the plan list's instances; Tactic Types hold their own copies
            renamePlatformInPlan(this.currentPlan, updatedPlatform.id.intID, updatedPlatform.name)
          },
          (error: unknown) => this.showSaveError(error)
        )
    },
    onDeleteClick(event: Event, platform: TacticPlatform) {
      const target = event.currentTarget
      const platformIntId = platform.id.intID
      this.deletingPlatformIntIds.push(platformIntId)

      // Fetch the platform for its current usage count; fall back to the generic warning.
      // The button stays disabled only during the fetch, so a double click can't open two popups.
      this.$store.getters.services.channels
        .getTacticPlatform(platformIntId)
        .then(
          (fetchedPlatform: TacticPlatform) => fetchedPlatform.tacticCount,
          () => null
        )
        .then((tacticCount: number | null) => {
          this.deletingPlatformIntIds = this.deletingPlatformIntIds.filter(
            (intId) => intId !== platformIntId
          )
          this.$confirm.require({
            target,
            message: platformDeleteMessage(tacticCount),
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            acceptClass: 'btn-delete-accept',
            accept: () => {
              this.deletePlatform(platformIntId)
            },
          })
        })
    },
    deletePlatform(platformIntId: number): Promise<void> {
      return this.$store.getters.services.channels
        .deleteTacticPlatform(platformIntId)
        .then(
          () =>
            // The API unlinked the platform from every Tactic Type and Tactic; reload the plan to match
            this.$store.dispatch('refreshCurrentPlan').then(() => {
              Vue.prototype.$toast.add({
                severity: AlertMessageSeverity.success,
                summary: 'Platform deleted.',
                life: 3000,
              })
            }),
          () => {
            this.showWarning('Error deleting platform.')
          }
        )
    },
  },
})
</script>
